import { sairDoLoginUnico, tokenRenovado } from './sessao-unica';
import type { Catalogo, Rotulo } from './vozes';

// API Base URL
// Um endereço só para a API. Quinze lugares montavam o próprio com
// `import.meta.env.VITE_API_URL` cru: na Hostinger a variável existe e passava,
// no build da VPS ela não existe, e as imagens pediam "undefined/uploads/...".
export const API_URL = import.meta.env.VITE_API_URL || '/api';

/**
 * Erro de uma chamada à API. É um `Error` comum — quem só lê `message`
 * continua funcionando — com o status HTTP, para a tela poder dizer a coisa
 * certa: sessão vencida (401) não é o mesmo que servidor fora do ar.
 * `status` 0 é falha de rede: o pedido nem chegou.
 */
export class ApiError extends Error {
    readonly status: number;

    constructor(message: string, status: number) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
    }
}

/** A falha foi de sessão (token vencido ou sem acesso), e não de rede ou servidor? */
export function sessaoVencida(erro: unknown): boolean {
    return erro instanceof ApiError && erro.status === 401;
}

/**
 * A data de hoje no aparelho do aluno (AAAA-MM-DD).
 *
 * Vai junto dos pedidos que mexem com a agenda de revisão: o servidor conta o
 * dia em UTC, que vira às 21h de Brasília, e sem isto o card "para amanhã"
 * voltava na mesma noite (ver `api/dia_do_aluno.php`).
 */
export function hojeLocal(): string {
    const d = new Date();
    const dois = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${dois(d.getMonth() + 1)}-${dois(d.getDate())}`;
}

// Helper para fazer requisições autenticadas
async function fetchAPI(endpoint: string, options: RequestInit = {}) {
    const pedir = (token: string | null) => {
        const headers: HeadersInit = {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` }),
            ...options.headers,
        };

        // Adicionar cache-buster para evitar cache agressivo da Hostinger (LiteSpeed/Cloudflare)
        const urlObj = new URL(`${window.location.origin}${API_URL}${endpoint}`);
        urlObj.searchParams.append('_t', Date.now().toString());

        return fetch(urlObj.toString(), { ...options, headers });
    };

    const token = localStorage.getItem('auth_token');
    let response: Response;
    try {
        response = await pedir(token);

        // 401 com login único: o token de uma hora pode ter vencido com a aba em
        // segundo plano (celular bloqueado no meio da revisão). Renova e repete
        // UMA vez — o pedido recusado na porta não chegou a ser processado. Se o
        // token não mudou, o 401 é de verdade (sem cadastro, conta desativada).
        if (response.status === 401 && token) {
            const novo = await tokenRenovado();
            if (novo && novo !== token) response = await pedir(novo);
        }
    } catch (e) {
        // Cancelamento e tempo esgotado seguem como vieram: quem chamou sabe tratar.
        if (e instanceof DOMException) throw e;
        throw new ApiError('Sem conexão com o servidor. Confira sua internet e tente de novo.', 0);
    }

    if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Request failed' }));
        throw new ApiError(error.error || `HTTP ${response.status}`, response.status);
    }

    return response.json();
}

// =====================================================
// AUTH API
// =====================================================

export interface LoginCredentials {
    email: string;
    senha: string;
    recaptchaToken?: string;
}

export interface RegisterData {
    nome: string;
    sobrenome: string;
    email: string;
    senha: string;
    tipo_usuario?: 'admin' | 'professor' | 'aluno';
}

export interface User {
    id: number;
    nome: string;
    sobrenome: string;
    email: string;
    tipo_usuario: 'admin' | 'professor' | 'aluno';
    foto?: string;
    ativo: boolean;
    cards_vistos?: number;
    /** Só na lista da equipe: quando o aluno respondeu um card pela última vez. */
    ultima_atividade?: string | null;
    /** Só na lista da equipe: respostas dos últimos 7 dias. */
    respostas_7d?: number;
    /** Só na lista da equipe: revisões vencidas, esperando o aluno. */
    cards_vencidos?: number;
    must_change_password?: boolean;
    /** Vazio = sem grupo, que o banco trata como Padrão. */
    grupo_ids?: number[];
}

export interface AuthResponse {
    token: string;
    user: User;
}

export const authAPI = {
    async login(credentials: LoginCredentials): Promise<AuthResponse> {
        const data = await fetchAPI('/auth.php', {
            method: 'POST',
            body: JSON.stringify(credentials),
        });

        // Salvar token
        if (data.token) {
            localStorage.setItem('auth_token', data.token);
            localStorage.setItem('user', JSON.stringify(data.user));
        }

        return data;
    },

    async register(userData: RegisterData): Promise<{ message: string; user_id: number }> {
        return fetchAPI('/auth.php?action=register', {
            method: 'POST',
            body: JSON.stringify(userData),
        });
    },

    async refreshCurrentUser(): Promise<User | null> {
        try {
            const data = await fetchAPI('/auth.php?action=me');
            if (data.user) {
                localStorage.setItem('user', JSON.stringify(data.user));
                return data.user;
            }
            return null;
        } catch (error) {
            console.error('Error refreshing user:', error);
            return null;
        }
    },

    logout() {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');

        /*
          Com login único, apagar só o que está aqui seria mentira: a sessão
          mora num cookie de domínio, e a próxima visita a leria e entraria de
          novo. Sair tem de encerrar a sessão compartilhada.

          Sem login único, resolve sem fazer nada.
        */
        void sairDoLoginUnico();
    },

    getCurrentUser(): User | null {
        const userStr = localStorage.getItem('user');
        return userStr ? JSON.parse(userStr) : null;
    },

    isAuthenticated(): boolean {
        return !!localStorage.getItem('auth_token');
    },

    async forgotPassword(email: string): Promise<{ success: boolean; message?: string }> {
        return fetchAPI('/forgot_password.php', {
            method: 'POST',
            body: JSON.stringify({ email }),
        });
    },

    async resetPassword(token: string, password: string): Promise<{ success: boolean; message?: string }> {
        return fetchAPI('/reset_password.php', {
            method: 'POST',
            body: JSON.stringify({ token, password }),
        });
    },

    async changePassword(password: string): Promise<{ success: boolean; message: string }> {
        const data = await fetchAPI('/auth.php?action=change-password', {
            method: 'POST',
            body: JSON.stringify({ password }),
        });

        const currentUser = this.getCurrentUser();
        if (currentUser) {
            localStorage.setItem('user', JSON.stringify({
                ...currentUser,
                must_change_password: false,
            }));
        }

        return data;
    }
};

// =====================================================
// USUARIOS API
// =====================================================

export const usuariosAPI = {
    async getAll(): Promise<{ usuarios: User[] }> {
        return fetchAPI('/usuarios.php');
    },

    async getById(id: number): Promise<{ usuario: User }> {
        return fetchAPI(`/usuarios.php?id=${id}`);
    },

    async update(id: number, data: Partial<User>): Promise<{ message: string; usuario: User }> {
        return fetchAPI(`/usuarios.php?id=${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    },

    /*
      Criar e remover passam por `fetchAPI`, como o resto: resposta de erro
      vira exceção com o recado da API. A tela chamava `fetch` direto e, no
      remover, nem olhava a resposta — a conta da equipe que o banco recusou
      apagar aparecia como "removido com sucesso".
    */
    async create(data: Record<string, unknown>): Promise<{ success: boolean; id: number }> {
        return fetchAPI('/usuarios.php', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    },

    async remove(id: number): Promise<{ success: boolean }> {
        return fetchAPI(`/usuarios.php?id=${id}`, {
            method: 'DELETE',
        });
    },

    async importCsv(usuarios: Array<{
        linha: number;
        nome: string;
        email: string;
        modulo_inicial: number;
    }>): Promise<{
        success: boolean;
        importados: Array<{
            linha: number;
            id: number;
            nome: string;
            email: string;
            modulo_inicial_id: number;
            modulo_inicial_nome: string;
            modulo_inicial_categoria_id: number;
        }>;
        total_importados: number;
        erros: Array<{ linha: number; nome?: string; email?: string; erro: string }>;
        total_erros: number;
    }> {
        return fetchAPI('/usuarios.php?action=import', {
            method: 'POST',
            body: JSON.stringify({ usuarios }),
        });
    },
};

// =====================================================
// ALUNOS API
// =====================================================

export interface Aluno {
    id: number;
    usuario_id: number;
    sexo?: 'Masculino' | 'Feminino' | 'Prefiro não dizer';
    aniversario?: string;
    data_ingresso?: string;
    nome?: string;
    sobrenome?: string;
    email?: string;
    foto?: string;
    modulo_inicial_nome?: string;
    modulo_inicial_categoria_id?: number;
}

export const alunosAPI = {
    async getByUsuarioId(usuarioId: number): Promise<{ has_profile: boolean; aluno: Aluno | null }> {
        return fetchAPI(`/alunos.php?usuario_id=${usuarioId}`);
    },

    async getEstatisticasAvancadas(alunoId?: number): Promise<any> {
        const query = alunoId ? `?aluno_id=${alunoId}` : '';
        return fetchAPI(`/estatisticas_avancadas.php${query}`);
    },

    async create(data: {
        usuario_id?: number;
        sexo?: string;
        aniversario?: string;
        data_ingresso?: string;
        modulo_inicial_nome?: string;
        modulo_inicial_categoria_id?: number;
    }): Promise<{ message: string; aluno: Aluno }> {
        return fetchAPI('/alunos.php', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    },

    async update(id: number, data: Partial<Aluno>): Promise<{ message: string; aluno: Aluno }> {
        return fetchAPI('/alunos.php', {
            method: 'PUT',
            body: JSON.stringify({ id, ...data }),
        });
    },
};

// =====================================================
// CARDS API
// =====================================================

export interface PalavraComCor {
    palabra: string;
    cor: string;
    corFundo?: string; // Cor de fundo opcional
}

export interface Card {
    id: number;
    texto_frances?: PalavraComCor[];
    alphanat?: PalavraComCor[];
    traducao_portugues?: string;
    texto_traduzido?: string;
    imagem_principal?: string;
    audio_path?: string;
    modulo?: string;
    categoria_id: number;
    categoria_nome?: string;
    categoria_cor?: string;
    categoria_icone?: string;
    ativo: boolean;
    ordem: number;
    // Nota: campo 'dificuldade' foi REMOVIDO - pertence apenas à tabela 'respostas'
    // Legacy fields (mantidos para compatibilidade)
    imagem_texto_frances?: string;
    imagem_traducao?: string;
}

export const cardsAPI = {
    async listForImport(categoriaId: number): Promise<Card[]> {
        // Omit modulo: the study endpoint applies SRS when a module is supplied.
        // Both active and inactive records must participate in duplicate detection.
        const responses = await Promise.all([0, 1].map(ativo => fetchAPI(
            `/cards.php?categoria_id=${categoriaId}&ativo=${ativo}`,
            { signal: AbortSignal.timeout(60000) },
        )));
        if (responses.some(response => !response.success || !Array.isArray(response.data))) {
            throw new Error('Não foi possível conferir os cards existentes. Nada foi importado.');
        }
        return responses.flatMap(response => response.data);
    },

    async createImported(data: Partial<Card>): Promise<number> {
        const response = await fetchAPI('/cards.php', {
            method: 'POST', body: JSON.stringify(data), signal: AbortSignal.timeout(60000),
        });
        const id = Number(response.id ?? response.data?.id);
        if (!response.success || !Number.isInteger(id) || id < 1) {
            throw new Error('O servidor não confirmou o ID do card. Confira o cadastro antes de continuar.');
        }
        return id;
    },

    async getAll(filters?: { categoria_id?: number; modulo?: string }): Promise<{ cards: Card[] }> {
        const params = new URLSearchParams();
        if (filters?.categoria_id) params.append('categoria_id', filters.categoria_id.toString());
        if (filters?.modulo) params.append('modulo', filters.modulo);
        // O que está vencido depende de que dia é hoje para quem estuda.
        params.append('hoje', hojeLocal());

        const response = await fetchAPI(`/cards.php?${params}`);
        return { cards: response.data || [] };
    },

    /**
     * Quantos cards ativos existem — no total, ou só os criados neste mês.
     *
     * Usa o `?action=count` que o `cards.php` já tinha e ninguém chamava. O
     * painel contava baixando a lista inteira (1,3 MB, com o conteúdo de cada
     * card) para ler só o `.length`.
     */
    async count(filtros?: { month?: 'current' }): Promise<number> {
        const mes = filtros?.month ? `&month=${filtros.month}` : '';
        const response = await fetchAPI(`/cards.php?action=count${mes}`);
        return Number(response.count) || 0;
    },

    /**
     * Quantos cards esperam o aluno hoje no módulo dele: revisões vencidas e
     * cards ainda não vistos. Para o painel dizer o que há para estudar sem
     * baixar a lista inteira.
     */
    async pendentes(): Promise<{ revisoes: number; novos: number; modulo: string | null; tem_perfil: boolean }> {
        const r = await fetchAPI(`/cards.php?action=pendentes&hoje=${hojeLocal()}`);
        // Servidor antigo responde a lista de cards neste endereço, sem os números.
        if (typeof r?.revisoes !== 'number' || typeof r?.novos !== 'number') {
            throw new Error('Este servidor ainda não informa os cards pendentes.');
        }
        return { revisoes: r.revisoes, novos: r.novos, modulo: r.modulo ?? null, tem_perfil: r.tem_perfil !== false };
    },

    async getById(id: number): Promise<{ card: Card }> {
        const response = await fetchAPI(`/cards.php?id=${id}`);
        return { card: response.data };
    },

    async create(data: Partial<Card>): Promise<{ message: string; card: Card }> {
        const response = await fetchAPI('/cards.php', {
            method: 'POST',
            body: JSON.stringify(data),
        });
        return { message: 'Card criado com sucesso', card: response.data };
    },

    async update(id: number, data: Partial<Card>): Promise<{ message: string; card: Card }> {
        const response = await fetchAPI(`/cards.php?id=${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
        return { message: 'Card atualizado com sucesso', card: response.data };
    },

    async delete(id: number): Promise<{ message: string }> {
        await fetchAPI(`/cards.php?id=${id}`, {
            method: 'DELETE',
        });
        return { message: 'Card removido com sucesso' };
    },
};

export async function uploadCardMedia(file: File): Promise<string> {
    const token = localStorage.getItem('auth_token');
    const form = new FormData();
    form.append('file', file);
    form.append('tipo', file.type.startsWith('audio/') ? 'audio' : 'card');
    const response = await fetch(`${API_URL.replace(/\/$/, '')}/upload.php`, {
        method: 'POST', headers: token ? { Authorization: `Bearer ${token}` } : {}, body: form,
        signal: AbortSignal.timeout(15 * 60000),
    });
    const result = await response.json();
    if (!response.ok || !result.success || typeof result.path !== 'string' || !result.path.startsWith('/uploads/cards/')) {
        throw new Error(result.error || 'O servidor não confirmou o upload da mídia.');
    }
    return result.path;
}

// =====================================================
// VOZES API
// =====================================================
//
// O servidor guarda as chaves e repassa: o modelo diz quem fala, a Fish gera o
// áudio. A escolha da voz a partir do rótulo é feita aqui, em lib/vozes.ts.

export const vozesAPI = {
    async catalogo(): Promise<Catalogo> {
        return (await fetchAPI('/vozes.php')).catalogo;
    },

    async rotular(frances: string, traducao: string, cena?: string): Promise<Rotulo> {
        return (await fetchAPI('/vozes.php?acao=rotulo', {
            method: 'POST',
            body: JSON.stringify({ frances, traducao, cena }),
        })).rotulo;
    },

    async audio(texto: string, vozId: string): Promise<Blob> {
        const token = localStorage.getItem('auth_token');
        const response = await fetch(`${API_URL.replace(/\/$/, '')}/vozes.php?acao=audio`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
            body: JSON.stringify({ texto, voz_id: vozId }),
            signal: AbortSignal.timeout(90000),
        });
        if (!response.ok) {
            const erro = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
            throw new Error(erro.error || `HTTP ${response.status}`);
        }
        return response.blob();
    },
};

// =====================================================
// CATEGORIAS API
// =====================================================

export interface Categoria {
    id: number;
    nome: string;
    descricao?: string;
    cor: string;
    icone?: string;
    ordem: number;
    ativo: boolean;
    exibir_alunos: boolean;
    /** Vazio = categoria sem grupo, visível para todos. */
    grupo_ids?: number[];
}

export const categoriasAPI = {
    async getAll(): Promise<{ categorias: Categoria[] }> {
        return fetchAPI('/categorias.php');
    },

    async getModulos(categoriaId: number): Promise<{ 
        categoria: { id: number; nome: string };
        modulos: Array<{
            modulo: string;
            total_cards: number;
            primeira_ordem: number;
            ultima_ordem: number;
        }>;
    }> {
        return fetchAPI(`/categorias.php?id=${categoriaId}&action=modulos`);
    },

    async create(data: Partial<Categoria>): Promise<{ message: string; id: number }> {
        const response = await fetchAPI('/categorias.php', {
            method: 'POST',
            body: JSON.stringify(data),
        });
        return { message: 'Categoria criada com sucesso', id: response.id };
    },

    async update(id: number, data: Partial<Categoria>): Promise<{ message: string; categoria: Categoria }> {
        return fetchAPI(`/categorias.php?id=${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    },

    async delete(id: number): Promise<{ message: string }> {
        await fetchAPI(`/categorias.php?id=${id}`, {
            method: 'DELETE',
        });
        return { message: 'Categoria removida com sucesso' };
    },
};

// =====================================================
// GRUPOS API
// =====================================================
//
// O aluno vê a categoria quando os dois compartilham um grupo. Categoria sem
// grupo aparece para todos; aluno sem grupo conta como o Padrão.

export interface Grupo {
    id: number;
    nome: string;
    padrao: boolean;
    total_alunos: number;
    total_categorias: number;
}

export const gruposAPI = {
    async listar(): Promise<Grupo[]> {
        // Lista vazia, e não `undefined`: servidor sem os grupos ainda (a
        // migração não rodou) não pode derrubar a tela de alunos inteira.
        return (await fetchAPI('/grupos.php')).grupos ?? [];
    },

    async criar(nome: string): Promise<number> {
        return (await fetchAPI('/grupos.php', { method: 'POST', body: JSON.stringify({ nome }) })).id;
    },

    async renomear(id: number, nome: string): Promise<void> {
        await fetchAPI(`/grupos.php?id=${id}`, { method: 'PUT', body: JSON.stringify({ nome }) });
    },

    async apagar(id: number): Promise<void> {
        await fetchAPI(`/grupos.php?id=${id}`, { method: 'DELETE' });
    },
};

// =====================================================
// MÓDULOS API
// =====================================================

export interface Modulo {
    id: number;
    categoria_id: number;
    nome: string;
    descricao?: string;
    ordem: number;
    ativo: boolean;
    categoria_nome?: string;
    total_cards?: number;
    created_at?: string;
    updated_at?: string;
}

export const modulosAPI = {
    async getAll(): Promise<{ modulos: Modulo[] }> {
        return fetchAPI('/modulos.php');
    },

    async getByCategoria(categoriaId: number): Promise<{
        categoria: { id: number; nome: string };
        modulos: Modulo[];
    }> {
        return fetchAPI(`/modulos.php?categoria_id=${categoriaId}`);
    },

    async getById(id: number): Promise<{ data: Modulo }> {
        return fetchAPI(`/modulos.php?id=${id}`);
    },

    async create(data: Partial<Modulo>): Promise<{ message: string; id: number }> {
        const response = await fetchAPI('/modulos.php', {
            method: 'POST',
            body: JSON.stringify(data),
        });
        return { message: 'Módulo criado com sucesso', id: response.id };
    },

    async update(id: number, data: Partial<Modulo>): Promise<{ message: string }> {
        await fetchAPI(`/modulos.php?id=${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
        return { message: 'Módulo atualizado com sucesso' };
    },

    async delete(id: number): Promise<{ message: string }> {
        await fetchAPI(`/modulos.php?id=${id}`, {
            method: 'DELETE',
        });
        return { message: 'Módulo removido com sucesso' };
    },
};

// =====================================================
// RESPOSTAS API
// =====================================================

export type Dificuldade = 'facil' | 'medio' | 'dificil' | 'impossivel';

export interface Resposta {
    id: number;
    card_id: number;
    aluno_id: number;
    dificuldade: Dificuldade;
    tempo_resposta: number;
    created_at: string;
}

export const respostasAPI = {
    async getAll(): Promise<{ respostas: Resposta[] }> {
        return fetchAPI('/respostas.php');
    },

    /**
     * `proxima_revisao` (AAAA-MM-DD) e `intervalo` (dias) dizem quando o card
     * volta. Opcionais: um servidor mais antigo não os manda.
     */
    async create(data: {
        card_id: number;
        dificuldade: Dificuldade;
        tempo_resposta: number;
    }): Promise<{ message: string; resposta: Resposta; proxima_revisao?: string; intervalo?: number }> {
        return fetchAPI('/respostas.php', {
            method: 'POST',
            body: JSON.stringify({ ...data, hoje: hojeLocal() }),
        });
    },
};

// =====================================================
// EVOLUCAO API
// =====================================================

export interface Evolucao {
    aluno_id: number;
    categoria_id?: number;
    categoria_nome?: string;
    categoria_cor?: string;
    cards_vistos: number;
    qtd_facil: number;
    qtd_medio: number;
    qtd_dificil: number;
    qtd_impossivel: number;
}

export interface Estatisticas {
    aluno_id: number;
    usuario_id: number;
    nome: string;
    sobrenome: string;
    total_cards_vistos: number;
    total_facil: number;
    total_medio: number;
    total_dificil: number;
    total_impossivel: number;
}

export const evolucaoAPI = {
    async getByUsuarioId(usuarioId: number): Promise<{
        has_profile: boolean;
        aluno_id?: number;
        evolucao_por_categoria: Evolucao[];
        estatisticas_gerais: Estatisticas;
    }> {
        return fetchAPI(`/evolucao.php?usuario_id=${usuarioId}`);
    },

    async getAll(): Promise<{ estatisticas: Estatisticas[] }> {
        return fetchAPI('/evolucao.php');
    },
};
